import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Input,
  Button,
  Text,
  Link,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Eye24Regular, EyeOff24Regular, ArrowResetRegular } from '@fluentui/react-icons';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { isSandbox, isProd } from '../config/appEnv';
import {
  getApiBaseUrl,
  getApiBaseOverride,
  setApiBaseOverride,
  clearApiBaseOverride,
  canOverrideBackend,
} from '../config/api';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: 'var(--main-content-bg)',
    ...shorthands.padding('24px', '16px'),
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('40px'),
    boxShadow: tokens.shadow16,
    boxSizing: 'border-box',
    '@media (max-width: 480px)': {
      ...shorthands.padding('28px', '20px'),
      boxShadow: tokens.shadow8,
    },
  },
  logo: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logoImage: {
    height: '60px',
    '@media (max-width: 480px)': {
      height: '50px',
    },
  },
  sandboxBadge: {
    display: 'block',
    width: 'fit-content',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: '12px',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    color: '#ffffff',
    backgroundColor: '#c2410c',
    ...shorthands.padding('2px', '8px'),
    ...shorthands.borderRadius('4px'),
  },
  title: {
    textAlign: 'center' as const,
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
    display: 'block',
    '@media (max-width: 480px)': {
      fontSize: '18px',
    },
  },
  subtitle: {
    textAlign: 'center' as const,
    display: 'block',
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    marginBottom: '32px',
    '@media (max-width: 480px)': {
      marginBottom: '24px',
    },
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  input: {
    // The inner field fill has square corners that poke past the root's
    // rounded border; a 1px inset pulls it back behind the curve.
    ...shorthands.padding('1px'),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: tokens.colorNeutralForeground1,
  },
  error: {
    backgroundColor: tokens.colorStatusDangerBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorStatusDangerBorder1),
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('10px', '12px'),
    color: tokens.colorStatusDangerForeground1,
    fontSize: '14px',
    wordBreak: 'break-all',
  },
  envHint: {
    display: 'block',
    textAlign: 'center' as const,
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-all',
  },
  overrideHint: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-all',
  },
  forgot: {
    textAlign: 'center' as const,
    marginTop: '4px',
    fontSize: '14px',
  },
});

export default function AdminLogin() {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAdminAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Manual backend target (preview/non-prod only). Pre-filled with any active
  // sticky override; blank means "use the default / auto-resolved backend".
  const [backendUrl, setBackendUrl] = useState(getApiBaseOverride() ?? '');

  const from = (location.state as any)?.from?.pathname || '/';

  const handleResetBackend = () => {
    clearApiBaseOverride();
    setBackendUrl('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError('Please enter your email and password');
      return;
    }

    // Apply the chosen backend before authenticating so the login request and
    // every call after it hit it. Empty input clears any existing override.
    if (canOverrideBackend()) {
      setApiBaseOverride(backendUrl);
    }

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(isProd() ? message : `${message} (API: ${getApiBaseUrl()})`);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>
          <img src="/logo-tradewinds.png" alt="Tradewinds Mechanical" className={styles.logoImage} />
        </div>

        {isSandbox() && <span className={styles.sandboxBadge}>SANDBOX</span>}

        <Text as="h1" className={styles.title}>Tradewinds Mechanical</Text>
        <Text className={styles.subtitle}>Sign in to the back office</Text>

        <form className={styles.form} onSubmit={handleSubmit}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.field}>
            <label className={styles.label}>Email</label>
            <Input
              className={styles.input}
              type="email"
              value={email}
              onChange={(_, data) => setEmail(data.value)}
              placeholder="you@example.com"
              size="large"
              disabled={isLoading}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>Password</label>
            <Input
              className={styles.input}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(_, data) => setPassword(data.value)}
              placeholder="Enter your password"
              size="large"
              disabled={isLoading}
              contentAfter={
                <Button
                  appearance="transparent"
                  icon={showPassword ? <EyeOff24Regular /> : <Eye24Regular />}
                  onClick={() => setShowPassword(!showPassword)}
                  size="small"
                  type="button"
                />
              }
            />
          </div>

          {canOverrideBackend() && (
            <div className={styles.field}>
              <label className={styles.label}>Backend URL</label>
              <Input
                className={styles.input}
                type="url"
                value={backendUrl}
                onChange={(_, data) => setBackendUrl(data.value)}
                placeholder={getApiBaseUrl()}
                size="large"
                disabled={isLoading}
                contentAfter={
                  backendUrl ? (
                    <Button
                      appearance="transparent"
                      icon={<ArrowResetRegular />}
                      onClick={handleResetBackend}
                      size="small"
                      type="button"
                      title="Reset to default backend"
                    />
                  ) : undefined
                }
              />
              <Text className={styles.overrideHint}>
                {backendUrl
                  ? 'Requests target this backend, pinned across reloads.'
                  : `Leave blank for the default (${getApiBaseUrl()}).`}
              </Text>
            </div>
          )}

          <Button appearance="primary" type="submit" size="large" disabled={isLoading}>
            {isLoading ? <Spinner size="tiny" /> : 'Sign In'}
          </Button>

          <div className={styles.forgot}>
            <Link onClick={() => navigate('/forgot-password')} disabled={isLoading}>
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
